/**
 * Hook for managing table column preferences with localStorage persistence
 */

import { useState, useCallback, useEffect } from 'react';
import {
  ColumnPreferences,
  getStoredPreferences,
  savePreferences,
  clearPreferences,
  mergeWithDefaults,
} from '../utils/viewPersistence';

export interface ColumnConfig {
  key: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  visible: boolean;
  sortable?: boolean;
  resizable?: boolean;
  draggable?: boolean;
}

interface UseTablePreferencesReturn {
  preferences: ColumnPreferences;
  columnConfigs: ColumnConfig[];
  updateColumnWidth: (columnKey: string, width: number) => void;
  updateColumnWidths: (widths: Record<string, number>) => void;
  updateColumnOrder: (columnKeys: string[]) => void;
  updateVisibleColumns: (columnKeys: string[], newColumnOrder?: string[]) => void;
  resetPreferences: () => void;
  getColumnWidth: (columnKey: string) => number;
  isColumnVisible: (columnKey: string) => boolean;
  getVisibleColumns: () => ColumnConfig[];
  applyViewPreferences: (viewData: {
    columnOrder?: string[];
    visibleColumns?: string[];
    columnWidths?: Record<string, number>;
  }) => void;
}

export const useTablePreferences = (
  entityType: string,
  defaultColumns: ColumnConfig[],
  useLocalStorage: boolean = true
): UseTablePreferencesReturn => {
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>(defaultColumns);

  // Initialize preferences from localStorage only if useLocalStorage is true
  const [preferences, setPreferences] = useState<ColumnPreferences>(() => {
    const defaults: ColumnPreferences = {
      columnWidths: defaultColumns.reduce(
        (acc, col) => ({ ...acc, [col.key]: col.defaultWidth }),
        {}
      ),
      columnOrder: defaultColumns.map((col) => col.key),
      visibleColumns: defaultColumns.filter((col) => col.visible).map((col) => col.key),
    };

    if (!useLocalStorage) {
      return defaults;
    }

    const stored = getStoredPreferences(entityType);
    return mergeWithDefaults(stored, defaults);
  });

  // When switching from localStorage to view mode (or vice versa), reset to appropriate defaults
  // NOTE: When switching to view mode (!useLocalStorage), we intentionally do NOT reset here
  // because the view data will be applied in a separate effect and would be overwritten by a reset.
  // The apply view data effect will set the preferences directly.
  useEffect(() => {
    console.log(`[useTablePreferences] useLocalStorage changed to ${useLocalStorage}`);

    if (useLocalStorage) {
      // Switching back to localStorage mode - load from localStorage
      console.log(`[useTablePreferences] Loading from localStorage`);
      const stored = getStoredPreferences(entityType);
      const defaults: ColumnPreferences = {
        columnWidths: defaultColumns.reduce(
          (acc, col) => ({ ...acc, [col.key]: col.defaultWidth }),
          {}
        ),
        columnOrder: defaultColumns.map((col) => col.key),
        visibleColumns: defaultColumns.filter((col) => col.visible).map((col) => col.key),
      };
      const merged = mergeWithDefaults(stored, defaults);
      console.log(`[useTablePreferences] Merged preferences from localStorage:`, merged);
      setPreferences(merged);
    }
    // When !useLocalStorage (switching to view mode), skip the reset here.
    // The apply view data effect will handle setting the correct preferences.
  }, [useLocalStorage, entityType, defaultColumns]);

  // Update column configurations based on preferences
  useEffect(() => {
    const updated = defaultColumns.map((col) => ({
      ...col,
      visible: preferences.visibleColumns.includes(col.key),
    }));
    setColumnConfigs(updated);
  }, [defaultColumns, preferences.visibleColumns]);

  const updateColumnWidth = useCallback(
    (columnKey: string, width: number) => {
      console.log(`[updateColumnWidth] Updating ${columnKey} to ${width}, useLocalStorage=${useLocalStorage}`);
      const updated: ColumnPreferences = {
        ...preferences,
        columnWidths: {
          ...preferences.columnWidths,
          [columnKey]: Math.max(width, defaultColumns.find((c) => c.key === columnKey)?.minWidth || 80),
        },
      };
      console.log(`[updateColumnWidth] New columnWidths:`, updated.columnWidths);
      setPreferences(updated);
      if (useLocalStorage) {
        console.log(`[updateColumnWidth] Saving to localStorage`);
        savePreferences(entityType, updated);
      } else {
        console.log(`[updateColumnWidth] NOT saving to localStorage (useLocalStorage=false)`);
      }
    },
    [preferences, entityType, defaultColumns, useLocalStorage]
  );

  // Batch update multiple column widths at once (to avoid state batching issues)
  const updateColumnWidths = useCallback(
    (widths: Record<string, number>) => {
      console.log(`[updateColumnWidths] Batch updating widths:`, widths, `useLocalStorage=${useLocalStorage}`);
      const newWidths: Record<string, number> = {};
      Object.entries(widths).forEach(([key, width]) => {
        newWidths[key] = Math.max(width, defaultColumns.find((c) => c.key === key)?.minWidth || 80);
      });

      const updated: ColumnPreferences = {
        ...preferences,
        columnWidths: {
          ...preferences.columnWidths,
          ...newWidths,
        },
      };
      console.log(`[updateColumnWidths] New columnWidths:`, updated.columnWidths);
      setPreferences(updated);
      if (useLocalStorage) {
        console.log(`[updateColumnWidths] Saving to localStorage`);
        savePreferences(entityType, updated);
      } else {
        console.log(`[updateColumnWidths] NOT saving to localStorage (useLocalStorage=false)`);
      }
    },
    [preferences, entityType, defaultColumns, useLocalStorage]
  );

  const updateColumnOrder = useCallback(
    (columnKeys: string[]) => {
      console.log(`[updateColumnOrder] New order:`, columnKeys, `useLocalStorage=${useLocalStorage}`);
      const updated: ColumnPreferences = {
        ...preferences,
        columnOrder: columnKeys,
      };
      setPreferences(updated);
      if (useLocalStorage) {
        console.log(`[updateColumnOrder] Saving to localStorage`);
        savePreferences(entityType, updated);
      } else {
        console.log(`[updateColumnOrder] NOT saving to localStorage (useLocalStorage=false)`);
      }
    },
    [preferences, entityType, useLocalStorage]
  );

  const updateVisibleColumns = useCallback(
    (columnKeys: string[], newColumnOrder?: string[]) => {
      console.log(`[updateVisibleColumns] New visible columns:`, columnKeys, `useLocalStorage=${useLocalStorage}`);
      // If a new column order is provided, use it; otherwise preserve the current order
      let finalColumnOrder = newColumnOrder || preferences.columnOrder.slice();

      // Ensure newly visible columns are in the order
      const visibleSet = new Set(columnKeys);
      finalColumnOrder = finalColumnOrder.filter((key) => visibleSet.has(key));

      // Add any visible columns that aren't in the order yet
      columnKeys.forEach((key) => {
        if (!finalColumnOrder.includes(key)) {
          finalColumnOrder.push(key);
        }
      });

      setPreferences((currentPrefs) => {
        // Use the current state from the functional update to avoid stale closures
        // This ensures we're using the LATEST columnWidths even if updateColumnWidths
        // was called in the same sync block
        const updated: ColumnPreferences = {
          ...currentPrefs,  // Use current state, not captured preferences
          columnOrder: finalColumnOrder,
          visibleColumns: columnKeys,
          // columnWidths is preserved from currentPrefs
        };
        console.log(`[updateVisibleColumns] Updated preferences:`, updated);

        if (useLocalStorage) {
          console.log(`[updateVisibleColumns] Saving to localStorage`);
          savePreferences(entityType, updated);
        } else {
          console.log(`[updateVisibleColumns] NOT saving to localStorage (useLocalStorage=false)`);
        }

        return updated;
      });
    },
    [preferences.columnOrder, entityType, useLocalStorage]
  );

  const resetPreferences = useCallback(() => {
    clearPreferences(entityType);
    const defaults: ColumnPreferences = {
      columnWidths: defaultColumns.reduce(
        (acc, col) => ({ ...acc, [col.key]: col.defaultWidth }),
        {}
      ),
      columnOrder: defaultColumns.map((col) => col.key),
      visibleColumns: defaultColumns.filter((col) => col.visible).map((col) => col.key),
    };
    setPreferences(defaults);
  }, [entityType, defaultColumns]);

  const getColumnWidth = useCallback(
    (columnKey: string): number => {
      return preferences.columnWidths[columnKey] || defaultColumns.find((c) => c.key === columnKey)?.defaultWidth || 150;
    },
    [preferences.columnWidths, defaultColumns]
  );

  const isColumnVisible = useCallback(
    (columnKey: string): boolean => {
      return preferences.visibleColumns.includes(columnKey);
    },
    [preferences.visibleColumns]
  );

  const getVisibleColumns = useCallback((): ColumnConfig[] => {
    return preferences.columnOrder
      .filter((key) => preferences.visibleColumns.includes(key))
      .map((key) => columnConfigs.find((c) => c.key === key))
      .filter((col) => col !== undefined) as ColumnConfig[];
  }, [preferences.columnOrder, preferences.visibleColumns, columnConfigs]);

  // Apply all view preferences at once (for view switching)
  const applyViewPreferences = useCallback(
    (viewData: {
      columnOrder?: string[];
      visibleColumns?: string[];
      columnWidths?: Record<string, number>;
    }) => {
      console.log('[applyViewPreferences] Applying view preferences:', viewData);
      // Create full preferences object with view data (no fallbacks - view data is authoritative)
      const updated: ColumnPreferences = {
        columnWidths: viewData.columnWidths || {},
        columnOrder: viewData.columnOrder || [],
        visibleColumns: viewData.visibleColumns || [],
      };
      console.log('[applyViewPreferences] Updated preferences:', updated);
      setPreferences(updated);
      // Always save to localStorage when applying view preferences
      console.log('[applyViewPreferences] Saving view preferences to localStorage');
      savePreferences(entityType, updated);
    },
    [entityType]
  );

  return {
    preferences,
    columnConfigs,
    updateColumnWidth,
    updateColumnWidths,
    updateColumnOrder,
    updateVisibleColumns,
    resetPreferences,
    getColumnWidth,
    isColumnVisible,
    getVisibleColumns,
    applyViewPreferences,
  };
};
